import { createContext, useContext, useState, useEffect, ReactNode, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/hooks/use-auth";
import type { Message } from "@shared/schema";

interface MessageNotificationContextType {
  hasNewMessages: boolean;
  setHasNewMessages: (value: boolean) => void;
  lastChecked: Date;
  setLastChecked: (date: Date) => void;
  isMessagingOpen: boolean;
  setIsMessagingOpen: (open: boolean) => void;
  messagingPanelRef: React.RefObject<HTMLDivElement>;
  currentTab: string;
  setCurrentTab: (tab: string) => void;
}

const MessageNotificationContext = createContext<MessageNotificationContextType | undefined>(undefined);

interface MessageNotificationProviderProps {
  children: ReactNode;
  jamNightId: number;
}

export function MessageNotificationProvider({ children, jamNightId }: MessageNotificationProviderProps) {
  const [hasNewMessages, setHasNewMessages] = useState(false);
  const [lastChecked, setLastChecked] = useState<Date>(new Date());
  const [isMessagingOpen, setIsMessagingOpen] = useState(false);
  const [lastViewedMessageId, setLastViewedMessageId] = useState<number | null>(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [currentTab, setCurrentTab] = useState("list");
  const messagingPanelRef = useRef<HTMLDivElement>(null);
  const { user } = useAuth();

  const { data: messages = [] } = useQuery<Message[]>({
    queryKey: [`/api/messages?jamNightId=${jamNightId}`],
    refetchInterval: 5000,
  });

  // Initialize lastViewedMessageId with the latest message ID when component mounts
  useEffect(() => {
    if (messages.length > 0 && lastViewedMessageId === null) {
      setLastViewedMessageId(messages[0].id);
      setIsInitialLoad(false);
    }
  }, [messages, lastViewedMessageId]);

  // Check if messaging panel is visible in viewport
  const isPanelInViewport = () => {
    if (!messagingPanelRef.current) return false;
    
    const rect = messagingPanelRef.current.getBoundingClientRect();
    const windowHeight = window.innerHeight || document.documentElement.clientHeight;
    
    // Panel is considered visible if any part of it is in the viewport
    return rect.bottom > 0 && rect.top < windowHeight;
  };

  // Update notifications based on new messages and panel state
  useEffect(() => {
    if (messages.length === 0 || isInitialLoad) return;

    const latestMessage = messages[0]; // Messages are ordered by createdAt DESC
    const currentUserName = user?.username || "";

    // Check if we should show notification based on multiScreenJams setting
    const shouldShowNotification = () => {
      // Message must be from someone else
      if (latestMessage.senderName === currentUserName) return false;
      
      // Message must be newer than last viewed
      if (lastViewedMessageId && latestMessage.id <= lastViewedMessageId) return false;
      
      if (user?.multiScreenJams) {
        // In multi-screen mode: show notification if chat tab is not selected
        return currentTab !== "chat";
      } else {
        // In single-screen mode: show notification if panel is collapsed or out of view
        return !isMessagingOpen || !isPanelInViewport();
      }
    };

    if (shouldShowNotification()) {
      setHasNewMessages(true);
    }
  }, [messages, lastViewedMessageId, isInitialLoad, currentTab, isMessagingOpen, user]);

  // Clear notifications when appropriate
  useEffect(() => {
    if (user?.multiScreenJams) {
      // In multi-screen mode: clear when chat tab is selected
      if (currentTab === "chat") {
        const latestMessage = messages[0];
        if (latestMessage) {
          setLastViewedMessageId(latestMessage.id);
          setHasNewMessages(false);
        }
        setLastChecked(new Date());
      }
    } else {
      // In single-screen mode: clear when panel is open and visible
      if (isMessagingOpen && isPanelInViewport()) {
        const latestMessage = messages[0];
        if (latestMessage) {
          setLastViewedMessageId(latestMessage.id);
          setHasNewMessages(false);
        }
        setLastChecked(new Date());
      }
    }
  }, [isMessagingOpen, messages, currentTab, user]);

  // Add scroll listener to detect when panel comes into/out of view (only for non-multiScreenJams mode)
  useEffect(() => {
    if (user?.multiScreenJams) return;

    const handleScroll = () => {
      if (isMessagingOpen && isPanelInViewport()) {
        // Panel is open and visible - update viewed state
        const latestMessage = messages[0];
        if (latestMessage) {
          setLastViewedMessageId(latestMessage.id);
          setHasNewMessages(false);
        }
        setLastChecked(new Date());
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isMessagingOpen, messages, user]);

  return (
    <MessageNotificationContext.Provider
      value={{
        hasNewMessages,
        setHasNewMessages,
        lastChecked,
        setLastChecked,
        isMessagingOpen,
        setIsMessagingOpen,
        messagingPanelRef,
        currentTab,
        setCurrentTab,
      }}
    >
      {children}
    </MessageNotificationContext.Provider>
  );
}

export function useMessageNotifications() {
  const context = useContext(MessageNotificationContext);
  if (context === undefined) {
    throw new Error("useMessageNotifications must be used within a MessageNotificationProvider");
  }
  return context;
}